import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import {
  BillingAddressFields,
  emptyBilling,
  type BillingDraft,
} from "@/components/give/BillingAddressFields";
import {
  RecognitionPicker,
  type RecognitionState,
} from "@/components/give/RecognitionPicker";
import { createGeneralOrder, type DonorPayload } from "@/services/give-api";
import { getStripe } from "@/services/stripe";
import { STEWARD_ENTITY } from "@shared/campaign";

const PRESETS = [25, 100, 500, 1_000, 5_000];

export function GeneralCheckout() {
  const navigate = useNavigate();
  const [billing, setBilling] = React.useState<BillingDraft>(() => emptyBilling("US"));
  const [amount, setAmount] = React.useState<number>(100);
  const [recognition, setRecognition] = React.useState<RecognitionState>({
    consent: "anonymous",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Warm Stripe.js + the public-config fetch in the background so the card
  // field on /give/:orderId renders without an extra wait.
  React.useEffect(() => {
    getStripe().catch(() => undefined);
  }, []);

  function billingFullName(): string {
    return `${billing.firstName} ${billing.lastName}`.trim();
  }

  async function onContinue(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (!amount || amount < 1) {
      setError("Please enter an amount of US$1 or more.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const donor: DonorPayload = {
        email: billing.email,
        firstName: billing.firstName,
        lastName: billing.lastName,
        phone: billing.phone || undefined,
        company: billing.company || undefined,
        address: {
          line1: billing.line1,
          line2: billing.line2 || undefined,
          city: billing.city,
          state: billing.state || undefined,
          postcode: billing.postcode,
          country: billing.country,
        },
      };
      const result = await createGeneralOrder({ amountUsd: amount, donor, recognition });
      sessionStorage.setItem(
        `give-checkout-${result.orderId}`,
        JSON.stringify({
          donor,
          recognition,
          amountUsd: result.amountUsd,
          wishItemName: undefined,
        }),
      );
      navigate(`/give/${result.orderId}`);
    } catch (err) {
      setError(String(err));
      setSubmitting(false);
    }
  }

  return (
    <PageShell
      title="General support"
      subtitle="Let the Foundation deploy the gift where it&rsquo;s most needed."
      stewardship="Give · general"
    >
      <Seo
        title="Support the .person Foundation"
        description=""
        path="/give/general"
        noindex
      />

      <Link
        to="/foundation/contribute"
        className="font-mono text-[10px] uppercase tracking-widest text-ink-3 hover:text-ink mb-8 inline-block"
      >
        ← Back to Contribute
      </Link>

      <p className="font-serif text-base text-ink-2 leading-relaxed mb-6 max-w-2xl">
        Memo code: <span className="font-mono text-ink">DPF-GENERAL</span>.
      </p>

      <div className="border border-hairline bg-paper-2 p-4 mb-8 text-xs font-mono text-ink-2 leading-relaxed max-w-2xl">
        <strong className="text-ink">{STEWARD_ENTITY}</strong> is the temporary fiduciary steward for monetary support to the Foundation. Gifts during the stewardship period are <strong className="text-ink">not tax-deductible</strong>.
      </div>

      <form onSubmit={onContinue} className="space-y-8 max-w-2xl">
        <section>
          <h2 className="font-serif text-xl text-ink mb-4">Amount</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESETS.map((p) => (
              <button
                type="button"
                key={p}
                onClick={() => setAmount(p)}
                className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border ${
                  amount === p
                    ? "bg-ink text-paper border-ink"
                    : "border-hairline text-ink hover:border-ink"
                }`}
              >
                ${p.toLocaleString("en-US")}
              </button>
            ))}
          </div>
          <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1" htmlFor="amount">
            Custom amount (USD)
          </label>
          <input
            id="amount"
            type="number"
            min={1}
            step={1}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            disabled={submitting}
            className="w-48 bg-paper border border-hairline px-3 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink"
          />
        </section>

        <section>
          <h2 className="font-serif text-xl text-ink mb-4">Billing details</h2>
          <BillingAddressFields value={billing} onChange={setBilling} disabled={submitting} />
        </section>

        <section>
          <RecognitionPicker
            billingFullName={billingFullName()}
            value={recognition}
            onChange={setRecognition}
            disabled={submitting}
          />
        </section>

        {error && (
          <p className="font-mono text-xs text-state-pending leading-relaxed">{error}</p>
        )}

        <button
          type="submit"
          disabled={submitting || !amount || amount < 1}
          className="w-full bg-ink text-paper px-6 py-4 font-mono text-xs uppercase tracking-widest hover:bg-ink-2 disabled:opacity-60"
        >
          {submitting ? "Preparing…" : `Continue — pay US$${amount.toLocaleString("en-US")}`}
        </button>
      </form>
    </PageShell>
  );
}
